import { Box, Flex, useBreakpointValue } from '@gazin/ecommerce-ui'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import BarraFiltrosProdutos from 'src/components/barraFiltrosProdutos'
import { ProductListDesktop } from 'src/components/conteudoBusca/Content/Desktop'
import { ProductListMobile } from 'src/components/conteudoBusca/Content/Mobile'
import { ProductDetails } from 'src/components/conteudoProdutos/productDetails'
import facebookActions from 'src/components/facebookPixel/facebookActions'
import FiltersProductsMobile from 'src/components/filtersProductsMobile'
import { LoadDepartaments } from 'src/components/loadDepartments'
import { TitleCategory } from 'src/components/loadDepartments/title-category'
import { FIRST_PAGE } from 'src/components/Pagination/PaginationGroupItems'
import { mapToItemProdutoVertical } from 'src/components/productVertical/utils'
import { searchProductsLinx } from 'src/services/linx-api'
import {
  Filter,
  Pagination,
  Product,
  SearchProductsResponse,
  SelectedFiltersByLinx,
  Suggestion,
} from 'src/services/linx-api/dtos/search-products-response'
import { searchProductsCategoryLinx } from 'src/services/linx-api/searchCategory'
import { CategoryReponse } from 'src/services/marketplace-api/dtos'
import { ProductsSortingKey } from 'src/util/enums/products-sorting-key'
import useLinxMeta from 'src/util/hooks/useLinxMeta'
import { identifyFilter } from 'src/util/searchAndCategory/identifyFilter'
import * as gtg from '../../services/gtag'
import * as gtm from '../../services/gtm'

const SearchNotFound = dynamic(
  () =>
    import('src/components/searchNotFound').then(
      (module) => module.SearchNotFound
    ),
  { ssr: false }
)

export type SearchContent = {
  categorySlug?: string
  category?: CategoryReponse | undefined
  isCategoryPage: boolean
  loadingApi?: boolean
  results: SearchProductsResponse
}

export type FilterProduct = {
  attribute?: string
  descricao?: string
  id_atributo: number | string
  id_valor_unidade: number | string
  label: string
  queryFiltro?: string
  tipo: string
  valor_maximo?: string | number | null
  valor_minimo?: string | number | null
}

export default function SearchAndCategoryPage({
  categorySlug,
  category,
  isCategoryPage,
  loadingApi,
  results,
}: SearchContent) {
  const [isGrid, setIsGrid] = useState(true)
  const [pagina, setPagina] = useState(FIRST_PAGE)
  const [configPaginas, setConfigPaginas] = useState({
    firstPage: 1,
    currentPage: 1,
    nextPage: 1,
    lastPage: 1,
    prevPage: 1,
  })
  const [selectedFilterByLinx, setSelectedFilterByLinx] =
    useState<SelectedFiltersByLinx[]>()
  const [produtos, setProdutos] = useState<Product[]>(results?.products)
  const [productsSize, setProductsSize] = useState<number>(results?.size)
  const [searchIdCategory, setSearchIdCategory] = useState(results?.searchId)
  const [suggestions, setSuggestions] = useState<Suggestion[] | undefined>(
    results?.suggestions
  )
  const [loading, setLoading] = useState(true)
  const [selectedFilter, setSelectedFilter] = useState<Partial<FilterProduct>>({
    descricao: '',
    id_atributo: '',
    id_valor_unidade: '',
    label: '',
    queryFiltro: '',
    tipo: '',
    valor_maximo: '',
    valor_minimo: '',
  })
  const [filters, setFilters] = useState<Filter[]>(results?.filters)
  const [arrayFilters, setArrayFilters] = useState<Partial<FilterProduct>[]>([])
  const [parametros, setParametros] = useState<{
    filtro: string
    ordenacao: ProductsSortingKey | string
  }>({
    filtro: '',
    ordenacao: ProductsSortingKey.Relevance,
  })
  const [accordionIndex, setAccordionIndex] = useState(0)
  const { setLinxSearch, setLinxCategory, setLinxSubCategory } = useLinxMeta()

  const route = useRouter()
  const routeParams = route.asPath.split('?')
  const URLParameters = new URLSearchParams(routeParams[1])
  const URLFilter = URLParameters.getAll('filtro')
  const URLOrder = URLParameters.get('order')
  const searchQuery = route?.query?.busca?.[0] ?? ''
  const queryPage = Number(route.query.pagina) || 1

  const isMobileDevice = useBreakpointValue(
    { base: true, md: true, lg: false },
    { fallback: 'lg' }
  )

  const defineTotalPages = useCallback(
    (pagination?: Pagination, currentPage?: number) => {
      const getPageNumber = (
        pageString: string | undefined
      ): number | undefined => {
        if (!pageString) return undefined

        const split = pageString.split('&')
        const splitPage = split[2].split('=')

        return splitPage[1] ? Number(splitPage[1]) : undefined
      }

      const lastPageNumber = getPageNumber(pagination?.last)
      const nextPageNumber = getPageNumber(pagination?.next)
      const firstPageNumber = getPageNumber(pagination?.first)

      const totalPaginas =
        firstPageNumber ?? nextPageNumber ?? lastPageNumber ?? pagina

      const previousPage = (currentPage ?? totalPaginas) - 1

      setPagina(currentPage ?? totalPaginas)
      setConfigPaginas((prevConfigPaginas) => ({
        ...prevConfigPaginas,
        firstPage: firstPageNumber ?? 1,
        currentPage: currentPage ?? 1,
        nextPage: nextPageNumber ?? 1,
        lastPage: lastPageNumber ?? 1,
        prevPage: previousPage ?? 1,
      }))
    },
    [pagina]
  )

  const searchProducts = useCallback(
    async (page: number, products?: Product[]) => {
      if (!isMobileDevice || page === FIRST_PAGE) {
        setLoading(true)
      }

      const productsInMemory = products === undefined ? produtos : products

      if (URLOrder && URLOrder != '') {
        setParametros({ ordenacao: URLOrder, filtro: URLFilter.join('&') })
      }

      const query = isCategoryPage
        ? route.query.categoria?.[0]
        : route.query.busca?.[0]

      if (!query) return setLoading(false)

      try {
        const pageData = isCategoryPage
          ? await searchProductsCategoryLinx({
              search: query,
              filter: URLFilter,
              order: URLOrder,
              page,
            })
          : await searchProductsLinx({
              search: query,
              filter: URLFilter,
              order: URLOrder,
              page,
            })

        if (!isCategoryPage) {
          setLinxSearch(
            pageData?.products,
            pageData?.searchId,
            route.query.busca?.[0]
          )
          facebookActions.search(route.query.busca?.[0], produtos)
        }

        setSuggestions(pageData?.suggestions)

        if (!pageData?.products) {
          setPagina(FIRST_PAGE)
          defineTotalPages(undefined, FIRST_PAGE)
          setProdutos([])
          return
        }

        let treatedFilters: Filter[] = pageData.filters

        if (pageData.filters) {
          if (isCategoryPage) {
            const categoryItem = pageData.filters
              .find((filter: any) => filter.attribute === 'Categoria')
              ?.values.find((item: any) => item.selected)
            const filtersCategory = categoryItem?.filters

            const filtersSubcategory =
              categoryItem?.idO !== categorySlug &&
              filtersCategory?.find((filter: any) => filter.selected)?.filters

            treatedFilters = pageData.filters.map((filter: any) =>
              filter.attribute === 'Categoria' && filtersCategory
                ? {
                    ...filter,
                    values: filtersSubcategory || filtersCategory,
                  }
                : filter
            )
          }
          setFilters(treatedFilters)
        }

        if (pageData.selectedFilters)
          setSelectedFilterByLinx(pageData.selectedFilters)

        const productsToRender = isMobileDevice
          ? productsInMemory.concat(pageData.products)
          : pageData.products

        setSearchIdCategory(pageData.searchId)
        setProdutos(productsToRender)
        setProductsSize(pageData.size)
        setPagina(page)
        defineTotalPages({ ...pageData.pagination }, page)
      } catch (error) {
        setPagina(FIRST_PAGE)
        defineTotalPages(undefined, FIRST_PAGE)
        setProductsSize(0)
      } finally {
        setLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      URLFilter,
      URLOrder,
      defineTotalPages,
      isCategoryPage,
      pagina,
      produtos,
      route.query.busca,
      route.query.categoria,
    ]
  )

  function changeSearchRoute(queryParams: string) {
    const urlPathName = isCategoryPage
      ? `/categoria/${route.query.categoria?.[0]}`
      : `/busca/${route.query.busca?.[0]}`

    route.push({ pathname: urlPathName, query: queryParams ?? '' }, undefined, {
      shallow: true,
    })
  }

  function changeOrdination(ordination: string) {
    setLoading(true)
    setPagina(FIRST_PAGE)
    let queryParamsFiltro = ''

    if (arrayFilters) {
      arrayFilters.map((filtro, key) => {
        if (queryParamsFiltro != '' && key <= arrayFilters.length) {
          queryParamsFiltro = `${queryParamsFiltro}&`
        }
        queryParamsFiltro = `${queryParamsFiltro}${filtro.queryFiltro}&`
      })
    }

    if (ordination && ordination != '') {
      queryParamsFiltro += `order=${ordination}`
    }
    changeSearchRoute(queryParamsFiltro)
  }

  const changeURL = (selectedFilters: Partial<FilterProduct>[]) => {
    if (selectedFilters) {
      let queryParamsFiltro = ''
      const filterSelectedWithDescription = selectedFilters.reduce(
        (acc, filtro) => {
          if (filtro.tipo == 'continuous') {
            acc[filtro.descricao!] = filtro.queryFiltro
            return acc
          }
          acc[filtro.descricao!] = [
            ...(acc[filtro.descricao!] || []),
            filtro.queryFiltro,
          ]
          return acc
        },
        {}
      )

      const returnedValueObject = Object.entries(
        filterSelectedWithDescription
      ).reduce((acc, [_, value]) => {
        if (Array.isArray(value)) {
          value.map((item: any) => {
            return acc.push(item as never)
          })
          return acc
        }
        acc.push(value as never)
        return acc
      }, [])

      queryParamsFiltro = returnedValueObject.join('&')

      if (queryParamsFiltro != '' && URLOrder && URLOrder != '') {
        queryParamsFiltro = `${queryParamsFiltro}&order=${URLOrder}`
      }
      if (queryParamsFiltro == '' && URLOrder && URLOrder != '') {
        queryParamsFiltro = `order=${URLOrder}`
      }

      changeSearchRoute(queryParamsFiltro)
    }
  }

  function clearFilters() {
    clearSelectedFilter()
    setArrayFilters([])
    changeURL([])
  }

  function clearSelectedFilter() {
    setSelectedFilterByLinx(undefined)
    setSelectedFilter({
      tipo: '',
      id_atributo: '',
      id_valor_unidade: '',
      valor_minimo: '',
      valor_maximo: '',
      descricao: '',
      label: '',
      queryFiltro: '',
    })
  }

  function removeFilter(index: number | any) {
    if (!arrayFilters?.length) return

    if (typeof index !== 'number') {
      const newArrayFilters = arrayFilters.filter(
        (item) =>
          item.id_atributo !== index?.id_atributo && item.label !== index?.label
      )

      setArrayFilters(newArrayFilters)

      changeURL(newArrayFilters)
      return
    }

    const removedFilter = arrayFilters.splice(index, 1)?.[0]
    const removedSelectedFilter =
      removedFilter?.id_atributo === selectedFilter?.id_atributo &&
      removedFilter?.id_valor_unidade === selectedFilter?.id_valor_unidade

    if (removedSelectedFilter) {
      clearSelectedFilter()
    }

    setArrayFilters(arrayFilters)

    changeURL(arrayFilters)
  }

  function selectFilter({
    attribute,
    id_atributo,
    id_valor_unidade,
    label,
    tipo,
    valor_maximo,
    valor_minimo,
  }: FilterProduct) {
    setLoading(true)
    let selectedFilters = arrayFilters || []
    let queryFiltro = ''
    let filterInformation: Partial<FilterProduct> = {}

    switch (tipo) {
      case 'discrete':
        queryFiltro = `filtro=d:${id_atributo}:${id_valor_unidade}`

        filterInformation = {
          tipo,
          id_atributo,
          id_valor_unidade,
          valor_minimo: null,
          valor_maximo: null,
          descricao: attribute,
          label,
          queryFiltro,
        }

        setSelectedFilter(filterInformation)
        break

      case 'continuous':
        queryFiltro = `filtro=c:${id_atributo}:${id_valor_unidade}:${valor_minimo}:${valor_maximo}`

        filterInformation = {
          tipo,
          id_atributo,
          id_valor_unidade,
          valor_minimo,
          valor_maximo,
          descricao: attribute,
          label: '',
          queryFiltro,
        }

        setSelectedFilter(filterInformation)
        break

      default:
        queryFiltro = ''
        setSelectedFilter({
          tipo: '',
          id_atributo: '',
          id_valor_unidade: '',
          valor_minimo: '',
          valor_maximo: '',
          descricao: '',
          label: '',
          queryFiltro,
        })

        selectedFilters = []
        setArrayFilters(selectedFilters)
        break
    }

    const filterSelected = arrayFilters
      ? arrayFilters.find(
          (filtro) => filtro.queryFiltro == filterInformation.queryFiltro
        )
      : null

    if (!filterSelected) {
      setPagina(FIRST_PAGE)

      selectedFilters.push(filterInformation)
      setArrayFilters(selectedFilters)

      return changeURL(selectedFilters)
    }
  }

  const selectFilterMobile = (filters: any[]) => {
    setLoading(true)

    const filterMapped = filters.map((item) => {
      if (item.tipo === 'discrete') {
        return {
          tipo: item.tipo,
          id_atributo: item.id_atributo,
          id_valor_unidade: item.id_valor_unidade,
          valor_minimo: null,
          valor_maximo: null,
          descricao: item.attribute,
          label: item.label,
          queryFiltro: `filtro=d:${item.id_atributo}:${item.id_valor_unidade}`,
        }
      }

      if (item.tipo === 'continuous') {
        return {
          tipo: item.tipo,
          id_atributo: item.id_atributo,
          id_valor_unidade: item.id_valor_unidade,
          valor_minimo: null,
          valor_maximo: null,
          descricao: item.attribute,
          label: item.label,
          queryFiltro: `filtro=c:${item.id_atributo}:${item.id_valor_unidade}:${item.valor_minimo}:${item.valor_maximo}`,
        }
      }

      return {
        tipo: '',
        id_atributo: '',
        id_valor_unidade: '',
        valor_minimo: '',
        valor_maximo: '',
        descricao: '',
        label: '',
        queryFiltro: '',
      }
    })

    changeURL(filterMapped)
  }

  useEffect(() => {
    const { query } = route

    if (typeof window !== 'undefined') {
      const [navigationEntry] = performance.getEntriesByType(
        'navigation'
      ) as PerformanceNavigationTiming[]
      const isReload = navigationEntry?.type === 'reload'

      if (isReload && route.query.pagina) {
        const newQuery = { ...query }
        delete newQuery.pagina
        route.replace(
          {
            pathname: route.pathname,
            query: newQuery,
          },
          undefined,
          { shallow: true }
        )
      }
    }
  }, [])

  useEffect(() => {
    if (loading) {
      return
    }
    window.scrollTo(0, 0)
  }, [loading])

  useEffect(() => {
    if (!category || !results) return

    category.categoria_pai
      ? setLinxSubCategory(category, searchIdCategory)
      : setLinxCategory(category, searchIdCategory)
  }, [
    results,
    category,
    setSearchIdCategory,
    setLinxSubCategory,
    searchIdCategory,
    setLinxCategory,
  ])

  useEffect(() => {
    defineTotalPages(results?.pagination, queryPage)
    identifyFilter({
      setArrayFilters,
      setSelectedFilter,
      URLFilter,
      filters,
      results,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryPage])

  useEffect(() => {
    const isFirstPage = (route.query.pagina ?? 1) === 1

    searchProducts(queryPage, isFirstPage ? [] : undefined)
    identifyFilter({
      setArrayFilters,
      setSelectedFilter,
      URLFilter,
      filters,
      results,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.query])

  useEffect(() => {
    identifyFilter({
      setArrayFilters,
      setSelectedFilter,
      URLFilter,
      filters,
      results,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, results])

  useEffect(() => {
    gtg.impressions(produtos)
    gtm.impressions(produtos)
  }, [produtos])

  if (!produtos?.length && !loading) {
    return <SearchNotFound search={searchQuery} />
  }

  return (
    <>
      {!isCategoryPage && (
        <Head>
          <meta name="robots" content="noindex, nofollow" />
        </Head>
      )}
      <Box
        alignItems="center"
        display={{ base: 'initial', lg: 'flex' }}
        flex="1"
        justifyContent="center"
      >
        <Box
          backgroundColor={{ base: '#f4f4f7', lg: '#ffffff' }}
          display={{ base: 'initial', lg: 'flex' }}
          flex="1"
          flexDirection="column"
          maxWidth="81.875rem"
          paddingTop={{ base: '1.5rem', xl: '2rem' }}
          paddingX="0.9375rem"
        >
          {!!loadingApi && <TitleCategory isMobile={!!isMobileDevice} />}

          {loading ? (
            <Flex paddingX={{ base: '0.75rem', lg: 0, xl: 0 }} width="100%">
              <LoadDepartaments isMobile={!!isMobileDevice} />
            </Flex>
          ) : (
            <>
              <Box
                alignItems="flex-start"
                display={{ base: 'flex', lg: 'grid' }}
                flex="1"
                flexDirection={{ base: 'column', lg: 'row' }}
                gridColumnGap="1rem"
                gridTemplateColumns="19.375rem 1fr"
                justifyContent="center"
                marginX={{ base: '0.75rem', lg: 0, xl: 0 }}
                paddingBottom={{
                  base: '1rem',
                  xl: category?.conteudo ? '0' : '3.9375rem',
                }}
              >
                {!isMobileDevice ? (
                  <>
                    <BarraFiltrosProdutos
                      arrayFilters={arrayFilters}
                      clearFilters={clearFilters}
                      filters={filters}
                      removeFilter={removeFilter}
                      selectedFilterByLinx={selectedFilterByLinx}
                      selectFilter={selectFilter}
                    />
                    <ProductListDesktop
                      alterarOrdenacao={changeOrdination}
                      configPaginas={configPaginas}
                      mapLinxProductToProductVertical={mapToItemProdutoVertical}
                      pagina={pagina}
                      parametros={parametros}
                      produtos={produtos}
                      resultsSize={productsSize}
                      suggestedSearches={suggestions}
                      isCategoryPage={isCategoryPage}
                    />
                  </>
                ) : (
                  <>
                    <FiltersProductsMobile
                      arrayFilters={arrayFilters}
                      changeOrdination={changeOrdination}
                      clearFilters={clearFilters}
                      filters={filters}
                      selectedFilterByLinx={selectedFilterByLinx}
                      selectFilter={selectFilterMobile}
                    />
                    <ProductListMobile
                      configPaginas={configPaginas}
                      isGrid={isGrid}
                      mapLinxProductToProductVertical={mapToItemProdutoVertical}
                      pagina={pagina}
                      parametros={parametros}
                      produtos={produtos}
                      resultsSize={productsSize}
                      setIsGrid={setIsGrid}
                      suggestedSearches={suggestions}
                      isCategoryPage={isCategoryPage}
                    />
                  </>
                )}
              </Box>
              {category?.conteudo && (
                <Flex justifyContent="space-between">
                  <ProductDetails
                    accordionIndex={accordionIndex}
                    setAccordionIndex={setAccordionIndex}
                    productContent={{
                      descricao: category?.conteudo,
                    }}
                    technicalSheet={[]}
                    showMore={true}
                    isCategoryPage={isCategoryPage}
                  />
                </Flex>
              )}
            </>
          )}
        </Box>
      </Box>
    </>
  )
}
